import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAsync } from 'react-use';

import { CatalogApi } from '@backstage/catalog-client';
import { getCompoundEntityRef } from '@backstage/catalog-model';
import { useSidebarOpenState } from '@backstage/core-components';
import {
  identityApiRef,
  useApi,
  useRouteRef,
} from '@backstage/core-plugin-api';
import { catalogApiRef, entityRouteRef } from '@backstage/plugin-catalog-react';

import {
  Avatar,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Tooltip,
  makeStyles,
  Theme,
} from '@material-ui/core';
import PersonIcon from '@material-ui/icons/Person';
import SettingsIcon from '@material-ui/icons/Settings';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
    minHeight: 56,
    padding: '0 0 0 24px',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
  },
  rootCollapsed: {
    justifyContent: 'center',
    padding: 0,
  },
  avatar: {
    width: 32,
    height: 32,
    fontSize: '0.85rem',
    fontWeight: 700,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  popper: {
    zIndex: theme.zIndex.tooltip,
  },
  paper: {
    minWidth: 160,
    boxShadow: theme.shadows[6],
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    marginBottom: theme.spacing(1),
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    fontSize: theme.typography.pxToRem(14),
    padding: theme.spacing(1.25, 2),
    color: theme.palette.text.primary,
    '& svg': {
      fontSize: 18,
      color: theme.palette.text.secondary,
    },
    '&:hover': {
      backgroundColor: `${theme.palette.primary.main}18`,
      color: theme.palette.primary.main,
      '& svg': { color: theme.palette.primary.main },
    },
  },
}));

function getInitials(name: string): string {
  const cleanName = name.replace(/[._]/g, ' ').trim();
  const parts = cleanName.split(/\s+/);

  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return parts[0].slice(0, 2).toUpperCase();
}

export const UserSidebarItem = () => {
  const classes = useStyles();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { isOpen: sidebarOpen } = useSidebarOpenState();

  const identityApi = useApi(identityApiRef);
  const catalogApi: CatalogApi = useApi(catalogApiRef);
  const catalogEntityRoute = useRouteRef(entityRouteRef);

  const { value: user } = useAsync(async () => {
    const { userEntityRef } = await identityApi.getBackstageIdentity();
    return await catalogApi.getEntityByRef(userEntityRef);
  }, []);

  if (!user) return null;

  const fullName = user.metadata.title || user.metadata.name;
  const initials = getInitials(fullName);
  const profilePath = catalogEntityRoute(getCompoundEntityRef(user));

  const handleToggle = () => setOpen(prev => !prev);
  const handleClose = () => setOpen(false);

  const handleNavigate = (path: string) => {
    handleClose();
    navigate(path);
  };

  return (
    <>
      <Tooltip title={fullName} placement="right" arrow>
        <button
          ref={anchorRef}
          onClick={handleToggle}
          className={`${classes.root} ${
            !sidebarOpen ? classes.rootCollapsed : ''
          }`}
          aria-haspopup="true"
          aria-expanded={open}
        >
          <Avatar className={classes.avatar}>{initials}</Avatar>
        </button>
      </Tooltip>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="top-start"
        transition
        className={classes.popper}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: 'left bottom' }}>
            <Paper className={classes.paper}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList disablePadding>
                  <MenuItem
                    className={classes.menuItem}
                    onClick={() => handleNavigate(profilePath)}
                  >
                    <PersonIcon />
                    Profile
                  </MenuItem>
                  <MenuItem
                    className={classes.menuItem}
                    onClick={() => handleNavigate('/settings')}
                  >
                    <SettingsIcon />
                    Settings
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};
